import React, { useState, useEffect } from 'react';
import { postAPI, postAPIForm } from '../../../../app/api';
import editIcon from "../../../../theme/assets/images/icon/edit-icon.svg"
import deleteIcon from "../../../../theme/assets/images/icon/delete-icon.svg"
import Swal from 'sweetalert2';

export default function HeroSectionsManager() {
    const [heroSections, setHeroSections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [selectedRoute, setSelectedRoute] = useState('home');

    // Consistent Initial State
    const initialFormState = {
        title: '',
        highlightText: '',
        highlightColor: '#FFD700',
        description: '',
        backgroundImage: null,
        mainImage: null,
        badgeImage: null,
        route: 'home',
        overlayColor: 'rgba(0, 51, 153, 0.7)',
        titleColor: '#ffffff',
        titleFont: 'Arial',
        titleSize: '48px',
        descriptionColor: '#ffffff',
        descriptionFont: 'Arial',
        descriptionSize: '18px',
    };

    const [formData, setFormData] = useState(initialFormState);

    const availableRoutes = [
        { value: 'home', label: 'Home' },
        { value: 'about', label: 'About' },
        { value: 'contact', label: 'Contact' },
        { value: 'gallery', label: 'Gallery' },
        { value: 'alumni', label: 'Alumni' },
        { value: 'infrastructure', label: 'Infrastructure' },
        { value: 'almamater', label: 'Alma Mater' }
    ];

    useEffect(() => {
        fetchHeroSections();
    }, []);

    const fetchHeroSections = async () => {
        try {
            setLoading(true);
            const response = await postAPI('hero-data/list', {});
            if (response?.status === 1) {
                setHeroSections(response?.data || []);
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch hero sections', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, files, type } = e.target;
        if (type === 'file') {
            setFormData(prev => ({ ...prev, [name]: files[0] }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const getHeroForRoute = () => heroSections.find(h => h.route === selectedRoute) || null;

    const handleEditRoute = () => {
        const hero = getHeroForRoute();
        if (hero) {
            setEditingId(hero.id);
            setFormData({
                ...initialFormState,
                ...hero,
                // Ensure we map legacy "image" field to backgroundImage if needed
                backgroundImage: hero.backgroundImage || hero.image || null,
            });
            setShowForm(true);
        }
    };

    const handleCreateNewRoute = () => {
        setEditingId(null);
        setFormData({ ...initialFormState, route: selectedRoute });
        setShowForm(true);
    };

    const handleSaveHero = async (e) => {
        e.preventDefault();
        try {
            // No validation required - title and description are optional

            let submitData = {
                ...formData,
                route: selectedRoute,
                created_by: 'Admin',
                updated_by: 'Admin'
            };

            if (editingId) submitData.id = editingId;

            // Handle Multi-Layer Image Uploads
            const imageFields = ['backgroundImage', 'mainImage', 'badgeImage'];
            for (const field of imageFields) {
                const fileValue = formData[field];
                if (fileValue && typeof fileValue !== 'string') {
                    const imgFormData = new FormData();
                    imgFormData.append('baseDir', 'hero');
                    imgFormData.append('filename', `hero_${field}_${selectedRoute}`);
                    imgFormData.append('original_file', fileValue);

                    const imgRes = await postAPIForm('file/uploadFile', imgFormData);
                    if (imgRes?.status === 1) {
                        submitData[field] = imgRes?.filepath;
                    }
                }
            }

            const response = await postAPI('hero-data/create', submitData);
            if (response?.status === 1) {
                Swal.fire('Success', 'Hero section saved successfully', 'success');
                resetForm();
                fetchHeroSections();
            } else {
                Swal.fire('Error', response?.msg || 'Failed to save', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleDeleteHero = async (id, routeLabel) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: `Delete the hero section for ${routeLabel}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Delete'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('hero-data/delete', { id });
                if (response?.status === 1) {
                    Swal.fire('Deleted!', 'Section removed.', 'success');
                    fetchHeroSections();
                }
            } catch (error) {
                Swal.fire('Error', 'Delete failed', 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData(initialFormState);
        setShowForm(false);
        setEditingId(null);
    };

    const currentHero = getHeroForRoute();

    return (
        <div className="container-fluid py-4">
            <div className="row">
                {/* Left Side: Route Selection */}
                <div className="col-md-3">
                    <div className="card shadow-sm border-0 mb-4">
                        <div className="card-header bg-primary text-white py-3">
                            <h6 className="mb-0">Navigation Routes</h6>
                        </div>
                        <div className="list-group list-group-flush">
                            {availableRoutes.map(route => (
                                <button
                                    key={route.value}
                                    onClick={() => {
                                        setSelectedRoute(route.value);
                                        setShowForm(false);
                                    }}
                                    className={`list-group-item list-group-item-action border-0 ${selectedRoute === route.value ? 'bg-light font-weight-bold text-primary' : ''}`}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span>{route.label}</span>
                                        {heroSections.some(h => h.route === route.value) && (
                                            <span className="badge rounded-pill bg-success" style={{ fontSize: '10px' }}>Active</span>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Side: Editor/View */}
                <div className="col-md-9">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                            <h5 className="mb-0 text-dark">
                                {availableRoutes.find(r => r.value === selectedRoute)?.label} Section
                            </h5>
                            <div className="d-flex gap-2">
                                {showForm ? (
                                    <button className="btn btn-sm btn-light border" onClick={resetForm}>Cancel</button>
                                ) : (
                                    <>
                                        {currentHero ? (
                                            <>
                                                <button className="btn btn-outline-primary btn-sm" onClick={handleEditRoute}>
                                                    <img src={editIcon} alt="edit" className="me-1" width="14" /> Edit
                                                </button>
                                                <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteHero(currentHero.id, selectedRoute)}>
                                                    <img src={deleteIcon} alt="delete" className="me-1" width="14" /> Delete
                                                </button>
                                            </>
                                        ) : (
                                            <button className="btn btn-primary btn-sm" onClick={handleCreateNewRoute}>+ Setup New</button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="card-body">
                            {loading ? (
                                <div className="text-center py-5">Loading...</div>
                            ) : showForm ? (
                                <form onSubmit={handleSaveHero}>
                                    <div className="card bg-light border-0 mb-4">
                                        <div className="card-body p-4">
                                            <h6 className="font-weight-bold mb-3">Background Image</h6>
                                            <p className="text-muted small mb-3">Upload a background image for the hero section of this page.</p>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <input type="file" className="form-control" name="backgroundImage" onChange={handleChange} accept="image/*" />
                                                    {formData.backgroundImage && typeof formData.backgroundImage === 'string' && (
                                                        <div className="mt-2">
                                                            <small className="text-muted">Current: {formData.backgroundImage.split('/').pop()}</small>
                                                            <div className="mt-2">
                                                                <img
                                                                    src={formData.backgroundImage.startsWith('http') ? formData.backgroundImage : `${process.env.REACT_APP_APIBRANDURL || 'https://beapi.becex.org/'}${formData.backgroundImage}`}
                                                                    alt="Current background"
                                                                    style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'cover', borderRadius: '8px' }}
                                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex gap-2">
                                        <button type="submit" className="btn btn-success px-4">Save</button>
                                        <button type="button" className="btn btn-light px-4 border" onClick={resetForm}>Cancel</button>
                                    </div>
                                </form>
                            ) : (
                                <div className="p-2">
                                    {currentHero ? (
                                        <div className="row align-items-center">
                                            <div className="col-md-7">
                                                <h3 className="mb-2">{currentHero.title}</h3>
                                                <p className="text-muted mb-4">{currentHero.description}</p>
                                                <div className="d-flex gap-3 small text-uppercase font-weight-bold text-primary">
                                                    <span>Font: {currentHero.titleFont}</span>
                                                    <span>Size: {currentHero.titleSize}</span>
                                                </div>
                                            </div>
                                            <div className="col-md-5">
                                                <div className="rounded border bg-light p-2 text-center shadow-sm">
                                                    {currentHero.backgroundImage ? (
                                                        <img src={currentHero.backgroundImage} className="img-fluid rounded" alt="preview" style={{ maxHeight: '200px' }} />
                                                    ) : (
                                                        <div className="py-5 text-muted small">No Background Image</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-5">
                                            <p className="text-muted mb-0">No hero section configured for this page yet.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}